import { useEffect, useState, useMemo, useCallback } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Plus, Pencil, Trash2, KeyRound, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { ROLES, useAuth } from "../contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const EMPTY = {
  username: "",
  email: "",
  name: "",
  password: "",
  role: "cashier",
  branch_id: "",
  consortium_id: "",
  assigned_branch_ids: [],
  active: true,
};

const usernameRe = /^[A-Za-z0-9._-]{3,32}$/;

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [consortiums, setConsortiums] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user for password reset
  const [resetResult, setResetResult] = useState(null); // { user, new_password }
  const [copied, setCopied] = useState(false);

  const isSuper = me?.role === "super_admin";

  const load = useCallback(async () => {
    const [u, b, c] = await Promise.all([
      api.get("/users"),
      api.get("/branches"),
      api.get("/consortiums"),
    ]);
    setUsers(u.data);
    setBranches(b.data);
    setConsortiums(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      consortium_id: isSuper ? "" : (me?.consortium_id || ""),
    });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username || "",
      email: u.email || "",
      name: u.name,
      password: "",
      role: u.role,
      branch_id: u.branch_id || "",
      consortium_id: u.consortium_id || "",
      assigned_branch_ids: u.assigned_branch_ids || [],
      active: u.active,
    });
    setOpen(true);
  };

  const branchesInScope = useMemo(() => {
    const cid = form.consortium_id;
    if (!cid) return branches; // no scope filter
    return branches.filter((b) => b.consortium_id === cid);
  }, [branches, form.consortium_id]);

  const save = async () => {
    if (!editing && !usernameRe.test(form.username)) {
      toast.error("Usuario: 3-32 caracteres, solo letras, números, . _ -");
      return;
    }
    if (!editing && form.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.role !== "super_admin" && !form.consortium_id) {
      toast.error("Debes seleccionar un consorcio");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.branch_id) payload.branch_id = null;
      if (!payload.email) payload.email = null;
      if (payload.role === "super_admin") payload.consortium_id = null;
      if (payload.role !== "supervisor") payload.assigned_branch_ids = [];
      if (editing) {
        if (!payload.password) delete payload.password;
        delete payload.username; // username edit is a separate operation; keep it simple
        await api.put(`/users/${editing.id}`, payload);
        // Persist branch assignment for supervisor separately if changed
        if (payload.role === "supervisor") {
          await api.post(`/users/${editing.id}/assign-branches`, { branch_ids: payload.assigned_branch_ids || [] });
        }
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminar usuario ${u.username || u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmReset = (u) => {
    setResetTarget(u);
    setResetResult(null);
    setCopied(false);
  };

  const runReset = async () => {
    if (!resetTarget) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${resetTarget.id}/reset-password`);
      setResetResult({ user: resetTarget, new_password: data.new_password });
      toast.success(`Nueva clave generada para @${resetTarget.username}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = async () => {
    if (!resetResult?.new_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar. Cópiala manualmente.");
    }
  };

  const closeReset = () => { setResetTarget(null); setResetResult(null); setCopied(false); };

  const canResetOf = (u) => {
    if (!me) return false;
    if (me.role === "super_admin") return u.id !== me.id;
    if (me.role === "admin") {
      return u.consortium_id === me.consortium_id && !["super_admin", "admin"].includes(u.role);
    }
    return false;
  };

  const consortiumName = (id) => consortiums.find((c) => c.id === id)?.name || "—";

  const toggleAssigned = (bid) => {
    setForm((f) => {
      const set = new Set(f.assigned_branch_ids || []);
      if (set.has(bid)) set.delete(bid); else set.add(bid);
      return { ...f, assigned_branch_ids: Array.from(set) };
    });
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Usuarios"
        subtitle="Gestión de cuentas y roles del sistema"
        testId="users-header"
        actions={
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 rounded-sm font-bold" data-testid="users-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
          </Button>
        }
      />

      <div className="p-6 sm:p-8">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Usuario</Th><Th>Nombre</Th><Th>Rol</Th>{isSuper && <Th>Consorcio</Th>}<Th>Sucursal</Th><Th>Estado</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const b = branches.find((x) => x.id === u.branch_id);
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.username || u.email}`}>
                    <td className="p-3 font-mono font-bold text-slate-950">@{u.username || "—"}</td>
                    <td className="p-3 font-semibold text-slate-900">{u.name}</td>
                    <td className="p-3 text-slate-700">{ROLES[u.role]}</td>
                    {isSuper && <td className="p-3 text-slate-700 text-xs">{consortiumName(u.consortium_id)}</td>}
                    <td className="p-3 text-slate-700">{b ? `${b.code} · ${b.name}` : <span className="text-slate-400">—</span>}</td>
                    <td className="p-3">
                      {u.active ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-green-100 text-green-700">Activo</span> :
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">Inactivo</span>}
                    </td>
                    <td className="p-3 text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="h-8 rounded-sm border-slate-300" data-testid={`user-edit-${u.username || u.email}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {canResetOf(u) && (
                        <Button size="sm" variant="outline" onClick={() => confirmReset(u)} className="h-8 rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`user-reset-${u.username || u.email}`} title="Restablecer contraseña">
                          <KeyRound className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => remove(u)} className="h-8 rounded-sm border-red-300 text-red-700" data-testid={`user-delete-${u.username || u.email}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && <tr><td colSpan={isSuper ? 7 : 6} className="p-8 text-center text-slate-500 text-sm">Sin usuarios</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="user-form-name" />
            </div>
            {!editing && (
              <>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Usuario</Label>
                  <Input
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="p. ej. cajeroaz01"
                    className="mt-1 rounded-sm font-mono lowercase"
                    autoCapitalize="none"
                    data-testid="user-form-username"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Email (opcional)</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="user-form-email" />
                </div>
              </>
            )}
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider">
                {editing ? "Nueva contraseña (opcional)" : "Contraseña"}
              </Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" data-testid="user-form-password" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLES).filter(([k]) => isSuper || k !== "super_admin").map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isSuper && form.role !== "super_admin" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Consorcio</Label>
                <Select value={form.consortium_id || undefined} onValueChange={(v) => setForm({ ...form, consortium_id: v, branch_id: "", assigned_branch_ids: [] })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="user-form-consortium"><SelectValue placeholder="Selecciona un consorcio" /></SelectTrigger>
                  <SelectContent>
                    {consortiums.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className={isSuper && form.role !== "super_admin" ? "col-span-2" : "col-span-1"}>
              <Label className="text-xs font-semibold uppercase tracking-wider">Sucursal (principal)</Label>
              <Select value={form.branch_id || "none"} onValueChange={(v) => setForm({ ...form, branch_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-form-branch"><SelectValue placeholder="Sin sucursal" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin sucursal</SelectItem>
                  {branchesInScope.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.role === "supervisor" && (
              <div className="col-span-2 border-t border-slate-200 pt-3">
                <Label className="text-xs font-semibold uppercase tracking-wider">Bancas que puede supervisar</Label>
                <p className="text-xs text-slate-500 mt-1 mb-2">
                  Deja vacío para que supervise todas las bancas del consorcio.
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-auto p-2 border border-slate-200 rounded-sm bg-slate-50">
                  {branchesInScope.length === 0 && <div className="col-span-2 text-xs text-slate-500 py-2 text-center">Selecciona un consorcio primero.</div>}
                  {branchesInScope.map((b) => {
                    const checked = (form.assigned_branch_ids || []).includes(b.id);
                    return (
                      <label key={b.id} className="flex items-center gap-2 text-xs cursor-pointer">
                        <input type="checkbox" checked={checked} onChange={() => toggleAssigned(b.id)} data-testid={`user-assign-${b.code}`} />
                        <span className="font-mono">{b.code}</span>
                        <span className="text-slate-500 truncate">{b.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="col-span-2 flex items-center gap-3 pt-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-form-active" />
              <Label className="text-sm">Usuario activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" disabled={saving}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 rounded-sm" disabled={saving} data-testid="user-form-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password modal */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && closeReset()}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-black tracking-tight">
              Restablecer contraseña
            </DialogTitle>
          </DialogHeader>
          {resetTarget && !resetResult && (
            <div className="space-y-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <div className="text-xs text-slate-500">Usuario</div>
                <div className="font-mono font-bold text-slate-950 text-base">@{resetTarget.username}</div>
                <div className="text-slate-600 mt-1 text-sm">{resetTarget.name} · {ROLES[resetTarget.role]}</div>
              </div>
              <p className="text-sm text-slate-600">
                Se generará una nueva contraseña temporal. Compártela con el usuario para que ingrese y la cambie.
              </p>
            </div>
          )}
          {resetTarget && resetResult && (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-green-700">
                  Nueva contraseña temporal
                </div>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="font-mono font-black text-2xl tracking-widest text-slate-950" data-testid="reset-password-value">
                    {resetResult.new_password}
                  </div>
                  <Button size="sm" variant="outline" className="rounded-sm border-slate-300" onClick={copyPassword} data-testid="reset-copy-btn">
                    {copied ? <Check className="w-4 h-4 text-green-700" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Copia esta clave ahora. No podrás verla nuevamente. El usuario podrá cambiarla al ingresar.
              </p>
            </div>
          )}
          <DialogFooter>
            {!resetResult && (
              <>
                <Button variant="outline" className="rounded-sm border-slate-300" onClick={closeReset} disabled={saving}>Cancelar</Button>
                <Button className="bg-amber-600 hover:bg-amber-700 rounded-sm text-white" onClick={runReset} disabled={saving} data-testid="reset-confirm-btn">
                  {saving ? "Generando…" : "Generar contraseña"}
                </Button>
              </>
            )}
            {resetResult && (
              <Button className="bg-blue-600 hover:bg-blue-700 rounded-sm" onClick={closeReset} data-testid="reset-close-btn">
                Listo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
